import { Modal, Tooltip } from '@ui-kit'
import { useEffect, useState } from 'react'
import { AddDataModal } from '../../../components/MultiLookupRowRender'
import { FieldValues } from 'react-hook-form'
import {
  getEntityDataByEntityNameIdApiForFilter,
  hideLoader,
  showLoader,
  useDispatch,
} from '@app-store'
import { DATA_CHECK, ENTITY_NAME } from '../../../utils/constants'
import { DataType } from '../../../types/MissingInformationSummary.types'
import { useAppSelector } from '../../../utils/agent.store'
import { useParams } from 'react-router-dom'
import { formatLocalDateWithZone } from '../../../utils/date.utils'
import { editEntityDataEmbeddedAPI } from '../../../apis/entityData.api'
import { DataCheckPopup } from './DataCheckPopup'

export const DataCheckIcon: React.FC<{
  caseDetails: FieldValues
  isViewMode: boolean
}> = ({ caseDetails, isViewMode }) => {
  const dispatch = useDispatch()
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const { caseId } = useParams()
  const [isDataCorrectionModalOpen, setIsDataCorrectionModalOpen] =
    useState<boolean>(false)

  const [totalPendingDataFields, setTotalPendingDataFields] =
    useState<number>(0)
  const [originalPendingData, setOriginalPendingData] = useState<DataType[]>([])
  const [pendingCorrectionData, setPendingCorrectionData] = useState<
    DataType[]
  >([])
  const [originalResolvedData, setOriginalResolvedData] = useState<DataType[]>(
    []
  )
  const [resolvedCorrectionData, setResolvedCorrectionData] = useState<
    DataType[]
  >([])
  const [overallDataCheckDetails, setOverallDataCheckDetails] = useState<
    DataType[]
  >([])
  const [originalCorrectionIds, setOriginalCorrectionIds] = useState<number[]>(
    []
  )

  const toggleDataCorrectionModal = (isCancel: boolean) => {
    if (isCancel) {
      setPendingCorrectionData(originalPendingData)
      setResolvedCorrectionData(originalResolvedData)
    }
    setIsDataCorrectionModalOpen((prev) => !prev)
  }

  const handleSave = async (isFinalCheck?: boolean) => {
    try {
      dispatch(showLoader())
      const payload = buildPayload(isFinalCheck)
      await editEntityDataEmbeddedAPI(
        ENTITY_NAME.CASE_LOWER,
        caseId as string,
        payload?.data,
        payload?.meta_data?.embedded
      )
      await getDataCorrectionInfo()
      toggleDataCorrectionModal(false)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleCancel = async () => {
    toggleDataCorrectionModal(true)
  }

  const buildPayload = (isFinalCheck?: boolean) => {
    const dataCheckArray = [
      overallDataCheckDetails.find((item) => !item?.final_check_done),
    ]?.map((item) => {
      const data = { ...item }
      delete data.start_date_label
      delete data.end_date_label
      return {
        ...data,
        end_date: new Date().toISOString(),
        final_check_done: true,
        _state: 'CHANGED',
      }
    })
    const currentIds: number[] = []
    const dataCorrectionPointsArray = [
      ...resolvedCorrectionData,
      ...pendingCorrectionData,
    ].map((item: DataType) => {
      currentIds.push(item?.id as number)
      const sanitizedItem = { ...item }
      const error_type = sanitizedItem?.error_type_label
      delete sanitizedItem.record_status
      delete sanitizedItem.field_status
      delete sanitizedItem.disabled
      delete sanitizedItem.resolved_on_label
      delete sanitizedItem.created_at_label
      delete sanitizedItem.error_type_label

      return {
        ...sanitizedItem,
        _state: (sanitizedItem as { _state?: string })._state ?? 'CHANGED',
        id: sanitizedItem?.id,
        error_type_id: sanitizedItem?.error_type,
        error_type: {
          ...(sanitizedItem?.error_type && {
            id: sanitizedItem?.error_type,
          }),
          value: error_type,
        },
      } as unknown as DataType
    })

    originalCorrectionIds?.forEach((id) => {
      if (!currentIds?.includes(id)) {
        dataCorrectionPointsArray?.push({
          id: id,
          _state: 'REMOVED',
        })
      }
    })

    return {
      meta_data: {
        entity_name: 'case',
        embedded: ['c_data_check_correction_points', 'c_data_check'],
      },
      data: {
        ...(isFinalCheck && { c_data_check: dataCheckArray }),
        c_data_check_correction_points: dataCorrectionPointsArray,
      },
    }
  }

  const getDataCorrectionInfo = async () => {
    try {
      const action = {
        payload: {
          entity_name: 'case',
          page: 0,
          size: 100,
          select: ['c_data_check_correction_points', 'c_data_check'],
          related_entities: {
            c_data_check_correction_points: {
              select: [
                'resolved_on',
                'resolved_by',
                'status',
                'field_name',
                'error_type',
                'created_at',
                'created_by',
                'description',
                'remark',
              ],
            },
            c_data_check: {
              select: [
                'cycle_number',
                'created_at',
                'end_date',
                'final_check_done',
              ],
            },
          },
        },
      }
      const response = await getEntityDataByEntityNameIdApiForFilter(
        action,
        caseId ?? ''
      )
      const originalIds: number[] = []
      const formattedCorrectionPoints =
        response?.data?.c_data_check_correction_points?.map(
          (item: DataType) => {
            originalIds.push(item?.id as number)
            return {
              ...item,
              created_at_label: item?.created_at
                ? formatLocalDateWithZone(item.created_at as string)
                : null,
              resolved_on_label: item?.resolved_on
                ? formatLocalDateWithZone(item.resolved_on as string)
                : null,
              field_status: item?.status
                ? DATA_CHECK.RESOLVED
                : DATA_CHECK.PENDING,
              error_type: (item?.error_type as { id?: number })?.id,
              error_type_label: (item?.error_type as { value?: string })?.value,
            }
          }
        ) ?? []

      const formattedDataCheck =
        response?.data?.c_data_check?.map((item: DataType) => ({
          ...item,
          start_date_label: item?.created_at
            ? formatLocalDateWithZone(item?.created_at as string)
            : null,
          end_date_label: item?.end_date
            ? formatLocalDateWithZone(item.end_date as string)
            : null,
        })) ?? []

      setOriginalCorrectionIds(originalIds)

      const pendingData = formattedCorrectionPoints?.filter(
        (item: DataType) => item?.status === false
      )

      const resolvedData = formattedCorrectionPoints?.filter(
        (item: DataType) => item?.status === true
      )

      setPendingCorrectionData(pendingData)
      setOriginalPendingData(pendingData)
      setResolvedCorrectionData(resolvedData)
      setOriginalResolvedData(resolvedData)
      setOverallDataCheckDetails(formattedDataCheck)
      setTotalPendingDataFields(pendingData?.length)
    } catch (err) {
      console.error(err)
    }
  }

  const handleIconClick = async () => {
    setIsDataCorrectionModalOpen(true)
  }

  useEffect(() => {
    getDataCorrectionInfo()
  }, [caseDetails])

  if (workflowDefinition?.global_variables?.new_flow && caseDetails.caseId) {
    return (
      <>
        <span className="flex ml-[0.625rem] items-center gap-[0.6875rem]">
          <Tooltip content={DATA_CHECK.DATA_CHECK_SUMMARY}>
            <button
              className={`flex items-center justify-center w-[1.125rem] h-[1.125rem] text-white rounded-sm bg-blue-600 font-bold text-xs`}
              type="button"
              onClick={handleIconClick}
            >
              {totalPendingDataFields}
            </button>
          </Tooltip>
        </span>
        {isDataCorrectionModalOpen && (
          <AddDataModal>
            <Modal
              isOpen={isDataCorrectionModalOpen}
              closeModal={() => toggleDataCorrectionModal(true)}
              showCloseButton={false}
              width="min-w-[60vw] max-w-[40%]"
              isDragable={true}
              isBackdropInteractive={true}
              isResizable={true}
              minResizeWidth={520}
              minResizeHeight={180}
            >
              <DataCheckPopup
                handleCancel={handleCancel}
                setPendingCorrectionData={setPendingCorrectionData}
                pendingCorrectionData={pendingCorrectionData}
                setResolvedCorrectionData={setResolvedCorrectionData}
                resolvedCorrectionData={resolvedCorrectionData}
                handleSave={handleSave}
                overallDataCheckDetails={overallDataCheckDetails}
                isViewMode={isViewMode}
              />
            </Modal>
          </AddDataModal>
        )}
      </>
    )
  }
}
